package com.qrkontaq.ui.theme

import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.silk.theme.colors.ColorMode

fun Color.Companion.hex(hex: String): Color {
    val cleanHex = hex.removePrefix("#").uppercase()
    require(cleanHex.length == 6) { "Hex color must be 6 characters (e.g., '7A9DE4')" }
    return Color.rgb(
        cleanHex.substring(0, 2).toInt(16),
        cleanHex.substring(2, 4).toInt(16),
        cleanHex.substring(4, 6).toInt(16)
    )
}

fun Color.Companion.hexa(hex: String, alpha: Float): Color {
    val cleanHex = hex.removePrefix("#").uppercase()
    require(cleanHex.length == 6) { "Hex color must be 6 characters (e.g., '7A9DE4')" }
    return Color.rgba(
        cleanHex.substring(0, 2).toInt(16),
        cleanHex.substring(2, 4).toInt(16),
        cleanHex.substring(4, 6).toInt(16),
        alpha
    )
}

fun Color.toRgbString(): String {
    val rgb = this.toRgb()
    return "rgb(${rgb.red}, ${rgb.green}, ${rgb.blue})"
}

fun Color.toHexString(): String {
    val rgb = this.toRgb()
    fun toHex(n: Int): String {
        val hex = n.toString(16).uppercase()
        return if (hex.length == 1) "0$hex" else hex
    }
    return "${toHex(rgb.red)}${toHex(rgb.green)}${toHex(rgb.blue)}"
}

fun Color.toRgbaString(alpha: Float): String {
    val rgb = this.toRgb()
    return "rgba(${rgb.red}, ${rgb.green}, ${rgb.blue}, $alpha)"
}

fun Color.withAlpha(alpha: Float): Color {
    val rgb = this.toRgb()
    return Color.rgba(rgb.red, rgb.green, rgb.blue, alpha)
}

object AppColors {

    val Primary = Color.hex("00A2FF")

    val ChatButtonBackground = Color.hexa("FFFFFF", 0.04f)
    val ChatButtonGradientStart = Color.hex("152265")
    val ChatButtonGradientEnd = Color.hexa("212144", 0f)
    val ChatButtonShadowInner1 = Color.hexa("FFFFFF", 0.06f)
    val ChatButtonShadowInner2 = Color.hexa("FFFFFF", 0.08f)
    val ChatButtonShadowDrop = Color.hexa("FFFFFF", 0.2f)

    val Error = Color.hex("FF6359")


    object Light {
        val Background = Color.hex("FFFFFF")
        val Surface = Color.hex("FAFAFA")
        val SurfaceVariant = Color.hex("F5F5F5")
        val OnBackground = Color.hex("000000")
        val OnSurface = Color.hex("000000")
        val OnPrimary = Color.hex("FFFFFF")
        
        val Border = Color.hex("E9E9E9")
        val Divider = Color.hex("E0E0E0")
        
        val TextPrimary = Color.hex("000000")
        val TextSecondary = Color.hex("646464")
        val TextTertiary = Color.hex("969696")
        
        val Shadow = Color.hexa("000000", 0.1f)
        val Overlay = Color.hexa("000000", 0.5f)
    }

    object Dark {

        val Background = Color.hex("0F172A")
        val Surface = Color.hex("141928")
        val SurfaceVariant = Color.hex("191E2D")
        val Sidebar = Color.hexa("1C3587",0.12f)
        val OnBackground = Color.hex("FFFFFF")
        val OnSurface = Color.hex("FFFFFF")
        val OnPrimary = Color.hex("FFFFFF")

        val Border = Color.hex("282D3C")
        val BorderLight = Color.hex("1E2332")
        val Divider = Color.hex("323746")

        val TextPrimary = Color.hex("FFFFFF")
        val TextSecondary = Color.hex("8699BE")
        val TextTertiary = Color.hex("BFC8D3")

        val Shadow = Color.hexa("000000", 0.4f)
        val Overlay = Color.hexa("000000", 0.7f)
    }

    val DarkTheme = ThemeColors(
        background = Dark.Background,
        surface = Dark.Surface,
        surfaceVariant = Dark.SurfaceVariant,
        onBackground = Dark.OnBackground,
        onSurface = Dark.OnSurface,
        onPrimary = Dark.OnPrimary,
        border = Dark.Border,
        divider = Dark.Divider,
        textPrimary = Dark.TextPrimary,
        textSecondary = Dark.TextSecondary,
        textTertiary = Dark.TextTertiary,
        shadow = Dark.Shadow,
        overlay = Dark.Overlay
    )

    fun getColors(colorMode: ColorMode): ThemeColors {
        return when (colorMode) {
            ColorMode.LIGHT -> ThemeColors(
                background = Light.Background,
                surface = Light.Surface,
                surfaceVariant = Light.SurfaceVariant,
                onBackground = Light.OnBackground,
                onSurface = Light.OnSurface,
                onPrimary = Light.OnPrimary,
                border = Light.Border,
                divider = Light.Divider,
                textPrimary = Light.TextPrimary,
                textSecondary = Light.TextSecondary,
                textTertiary = Light.TextTertiary,
                shadow = Light.Shadow,
                overlay = Light.Overlay
            )
            ColorMode.DARK -> DarkTheme
        }
    }
}

data class ThemeColors(
    val background: Color,
    val surface: Color,
    val surfaceVariant: Color,
    val onBackground: Color,
    val onSurface: Color,
    val onPrimary: Color,
    val border: Color,
    val divider: Color,
    val textPrimary: Color,
    val textSecondary: Color,
    val textTertiary: Color,
    val shadow: Color,
    val overlay: Color
)
